import React from "react";
import { Box } from "@mui/material";
import Sidebar from "./sidebar/Sidebar";
import Navbar from "./Navbar";
import {
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_COLLAPSED,
} from "./sidebar/sidebarWidth";

const COLLAPSE_KEY = "sidebarCollapsed";

/**
 * The frame every signed-in page sits in — customer panel and agent panel
 * alike.
 *
 * There were two of these. They already shared the Sidebar, but only one of
 * them passed it the collapse handler, so the rail folded away on the
 * customer side and not on the agent's; one offset the content with a margin
 * and the other with a flex row, so the two panels breathed differently. The
 * only real difference between the panels is which list of links the sidebar
 * shows, and that is what `isAgentPanel` selects.
 */
const DashboardLayout = ({
  isAgentPanel = false,
  showNavbar = true,
  hideSidebarOnDesktop = false,
  ...props
}) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Whether the sidebar is collapsed is a preference, so it survives a
  // reload and a move between pages — and between the two panels.
  const [collapsed, setCollapsed] = React.useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch (e) {
      return false;
    }
  });

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleCollapse = () => {
    setCollapsed((was) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, was ? "0" : "1");
      } catch (e) {
        // site data blocked; the sidebar still collapses for this visit
      }
      return !was;
    });
  };

  const railWidth = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

  return (
    <div>
      <Sidebar
        isAgentPanel={isAgentPanel}
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
        hideSidebarOnDesktop={hideSidebarOnDesktop}
        collapsed={collapsed}
        onCollapse={handleCollapse}
      />
      <Box
        component="main"
        sx={{
          /* The offset follows the drawer's real width, from the one place
             that defines it. */
          ml: { md: hideSidebarOnDesktop ? 0 : `${railWidth}px`, xs: 0 },
          minWidth: 0,
          px: { xs: 2, md: 3 },
          /* The page header carries its own breathing room now that it
             sticks to the top: padding here would leave a gap above it for
             the content to scroll into. */
          paddingTop: showNavbar ? 0 : { xs: 2, md: 3.5 },
          pb: 4,
          transition: "margin-left .2s ease",
        }}
      >
        {showNavbar && (
          <Navbar
            headingTitle={props.navTitle}
            headingSubtitle={props.navSubTitle}
            headingAction={props.navAction}
            handleDrawerToggle={handleDrawerToggle}
          />
        )}
        {props.children}
      </Box>
    </div>
  );
};

export default DashboardLayout;
